import { replace } from "lodash";
import numeral from "numeral";

// ----------------------------------------------------------------------

// Configurar el idioma para utilizar el punto como separador de miles
numeral.register("locale", "custom", {
  delimiters: {
    thousands: ".",
    decimal: ",",
  },
  abbreviations: {
    thousand: "K",
    million: "M",
    billion: "B",
    trillion: "T",
  },
  ordinal: function (number) {
    return number === 1 ? "er" : "ème";
  },
  currency: {
    symbol: "$",
  },
});

// Establecer el idioma por defecto
numeral.locale("custom");

export function formatAsCLP(amount) {
  const formatter = new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
  });
  return formatter.format(isNaN(Number(amount)) ? 0 : amount);
}

// Función para formatear números como moneda
export function fCurrency(number) {
  return numeral(number).format(Number.isInteger(number) ? "$0,0" : "$0,0.00");
}

export function fPercent(number) {
  return numeral(number / 100).format("0.0%");
}

export function formatDecimal(number) {
  const num = typeof number === "string" ? parseFloat(number) : number;

  if (isNaN(num)) return "0";

  const parts = num.toString().split(".");

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return parts.length > 1 ? parts.join(",") : parts[0];
}

export function fNumber(number) {
  return numeral(number).format();
}

export function fShortenNumber(number) {
  return replace(numeral(number).format("0.00a"), ".00", "");
}

export function fData(number) {
  return numeral(number).format("0.0 b");
}
export function formatoFecha() {
  let date = new Date();

  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  let fechaFinal = String(year) + "/";

  if (month < 10) {
    fechaFinal = fechaFinal + "0" + String(month) + "/";
  } else {
    fechaFinal = fechaFinal + String(month) + "/";
  }

  if (day < 10) {
    fechaFinal = fechaFinal + "0" + String(day);
  } else {
    fechaFinal = fechaFinal + String(day);
  }

  return fechaFinal;
}

export function formatoFecha2() {
  let date = new Date();

  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  let fechaFinal = String(year) + "-";

  if (month < 10) {
    fechaFinal = fechaFinal + "0" + String(month) + "-";
  } else {
    fechaFinal = fechaFinal + String(month) + "-";
  }

  if (day < 10) {
    fechaFinal = fechaFinal + "0" + String(day);
  } else {
    fechaFinal = fechaFinal + String(day);
  }

  return fechaFinal;
}

// ────────────────────────────────────────────────────────────────────────────
// SYNC con taxticbackend\src\utils\formatNumber.js (MONEDA_ALIAS, formatDecimal,
// formatValorPlan): mantener idénticos en ambos archivos para asegurar paridad
// de formato entre PDF (backend) y UI (frontend).
// ────────────────────────────────────────────────────────────────────────────

// Mapeo de nombres de BD a nombres normalizados para formateo
const MONEDA_ALIAS = {
  PESO: "CLP",
  DOLAR: "USD",
  EURO: "EUR",
};

/**
 * Formatea un monto con su moneda. Soporta dos estilos visuales:
 *
 * - estilo "v1" (default): símbolo y prefijo. Ej: "$100.000" / "UF 100,50" /
 *   "US$ 120,00" / "€50,00" / "UTM 5,00". Usa Intl.NumberFormat para USD/EUR.
 *   Empty solo cuando valor es null/undefined (preserva contrato legacy:
 *   `valor === 0` retorna "$0", no em dash). Devuelve em dash "—" si vacío.
 *
 * - estilo "v2": código ISO como sufijo. Ej: "$100.000 CLP" / "100,5 UF" /
 *   "$120 USD" / "€50 EUR". No fuerza decimales (UF "100,5", no "100,50").
 *   Empty cuando valor es null/undefined/""/0. emptyAs configurable
 *   (default em dash, V2 callers típicos pasan null para no renderizar).
 *
 * Acepta nombres BD ("PESO", "DOLAR", "EURO") o ISO ("CLP", "UF", "USD",
 * "EUR", "UTM"). UTM solo está soportado en estilo V1.
 *
 * @param {number|string} valor
 * @param {string} tipoMoneda
 * @param {Object} [opciones]
 * @param {"v1"|"v2"} [opciones.estilo="v1"]
 * @param {string|null} [opciones.emptyAs="—"]
 * @returns {string|null}
 *
 * @example
 * formatValorPlan(100000, "CLP")                                // "$100.000"
 * formatValorPlan(100, "UF")                                    // "UF 100"
 * formatValorPlan(100.5, "UF")                                  // "UF 100,5"
 * formatValorPlan(120, "USD")                                   // "US$ 120,00"
 * formatValorPlan(0, "CLP")                                     // "$0"   (preserva legacy)
 * formatValorPlan(null, "CLP")                                  // "—"
 * formatValorPlan(100000, "CLP", { estilo: "v2" })              // "$100.000 CLP"
 * formatValorPlan(100.5, "UF",  { estilo: "v2" })               // "100,5 UF"
 * formatValorPlan(0, "CLP", { estilo: "v2", emptyAs: null })    // null
 */
export function formatValorPlan(valor, tipoMoneda, opciones = {}) {
  const { estilo = "v1", emptyAs = "—" } = opciones;

  const isEmpty =
    estilo === "v2"
      ? valor == null || valor === "" || valor === 0
      : valor == null;
  if (isEmpty) return emptyAs;

  const num = isNaN(Number(valor)) ? 0 : Number(valor);
  const monedaRaw = (tipoMoneda || "CLP").toUpperCase();
  const moneda = MONEDA_ALIAS[monedaRaw] || monedaRaw;

  if (estilo === "v2") {
    switch (moneda) {
      case "CLP":
        return `$${num.toLocaleString("es-CL")} CLP`;
      case "UF":
        return `${num.toLocaleString("es-CL", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} UF`;
      case "USD":
        return `$${num.toLocaleString("es-CL")} USD`;
      case "EUR":
        return `€${num.toLocaleString("es-CL")} EUR`;
      default:
        return `${num.toLocaleString("es-CL")} ${monedaRaw}`.trim();
    }
  }

  // estilo v1 (default — comportamiento legacy preservado)
  switch (moneda) {
    case "CLP":
      return formatAsCLP(num);

    case "UF":
    case "UTM":
      return `${moneda} ${formatDecimal(Number.isInteger(num) ? num : num.toFixed(2))}`;

    case "USD":
      return new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "USD",
        currencyDisplay: "symbol",
      }).format(num);

    case "EUR":
      return new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "EUR",
        currencyDisplay: "symbol",
      }).format(num);

    default:
      return `${monedaRaw} ${formatDecimal(num)}`;
  }
}

/**
 * Agrupa valores de servicios por moneda y calcula el total de cada una.
 *
 * @param {Array<{valor: number, tipoMoneda: string}>} servicios
 * @returns {Array<{moneda: string, total: number}>}
 *
 * @example
 * calcularTotalesPorMoneda([
 *   { valor: 100, tipoMoneda: "UF" },
 *   { valor: 50000, tipoMoneda: "PESO" },
 *   { valor: 200, tipoMoneda: "UF" },
 * ])
 * // → [{ moneda: "UF", total: 300 }, { moneda: "PESO", total: 50000 }]
 */
export function calcularTotalesPorMoneda(servicios) {
  if (!servicios || servicios.length === 0) return [];
  const agrupado = {};
  servicios.forEach((s) => {
    const moneda = s.tipoMoneda || "PESO";
    const val = Number(s.valor) || 0;
    agrupado[moneda] = (agrupado[moneda] || 0) + val;
  });
  return Object.entries(agrupado).map(([moneda, total]) => ({ moneda, total }));
}
